import * as React from 'react'

import { ChevronDownIcon } from 'lucide-react'
import { action, makeObservable, observable } from 'mobx'
import { observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'

import Localization from '~/client/src/shared/localization/LocalizationManager'

import {
  DEFAULT_SIGNATURE_FONT,
  SIGNATURE_FONTS,
  SignatureFont,
} from './signatureFonts'

interface IProps {
  onSignatureChange: (
    dataUrl: string | null,
    fontFamily: string,
    typedText: string,
  ) => void
  width?: number
  height?: number
}

@observer
export default class SignatureTyped extends React.Component<IProps> {
  private canvasRef = React.createRef<HTMLCanvasElement>()
  private inputRef = React.createRef<HTMLInputElement>()

  @observable private text = ''
  @observable private selectedFont: SignatureFont = DEFAULT_SIGNATURE_FONT
  @observable private isStyleMenuOpen = false
  @observable private currentDataUrl: string | null = null

  public constructor(props: IProps) {
    super(props)
    makeObservable(this)
  }

  public render() {
    const { width = 400, height = 200 } = this.props

    return (
      <div className="signature-typed col">
        <div
          className="signature-typed-preview relative row x-center y-center"
          style={{ minHeight: height }}
          onClick={this.focusInput}
        >
          {this.currentDataUrl ? (
            <img
              src={this.currentDataUrl}
              alt={Localization.translator.signaturePreview}
              className="signature-typed-image"
            />
          ) : (
            <span
              className="signature-typed-placeholder"
              style={{ fontFamily: this.selectedFont.family }}
            >
              {Localization.translator.typeHere}
            </span>
          )}

          <div className="signature-font-selector absolute">
            <button
              type="button"
              className="signature-font-button row y-center"
              onClick={this.toggleStyleMenu}
            >
              <span>{Localization.translator.editStyle}</span>
              <span className="ml4">
                <ChevronDownIcon size={16} />
              </span>
            </button>

            {this.isStyleMenuOpen && (
              <div className="signature-font-menu col absolute">
                {SIGNATURE_FONTS.map(font => (
                  <button
                    type="button"
                    key={font.name}
                    className={classList({
                      'signature-font-option row y-center': true,
                      selected: font.name === this.selectedFont.name,
                    })}
                    style={{ fontFamily: font.family }}
                    onClick={() => this.selectFont(font)}
                  >
                    {font.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="signature-typed-controls row y-center mt12">
          <input
            ref={this.inputRef}
            type="text"
            className="signature-typed-input"
            placeholder={Localization.translator.typeHere}
            value={this.text}
            onChange={this.handleTextChange}
            autoComplete="off"
          />
        </div>

        <canvas
          ref={this.canvasRef}
          width={width}
          height={height}
          style={{ display: 'none' }}
        />
      </div>
    )
  }

  public clear = () => {
    this.setText('')
    this.props.onSignatureChange(null, this.selectedFont.family, '')
  }

  @action.bound
  public initialize(text: string, fontFamily?: string) {
    this.text = text || ''
    if (fontFamily) {
      const matchingFont = SIGNATURE_FONTS.find(f => f.family === fontFamily)
      if (matchingFont) {
        this.selectedFont = matchingFont
      }
    }
    if (this.text) {
      const dataUrl = this.renderTextToCanvas()
      this.currentDataUrl = dataUrl
      this.props.onSignatureChange(dataUrl, this.selectedFont.family, this.text)
    }
  }

  public getDataUrl = (): string | null => {
    if (!this.text.trim()) return null
    return this.renderTextToCanvas()
  }

  private focusInput = () => {
    this.inputRef.current?.focus()
  }

  @action.bound
  private handleTextChange(event: React.ChangeEvent<HTMLInputElement>) {
    this.setText(event.target.value)
  }

  @action
  private setText(value: string) {
    this.text = value
    const dataUrl = this.renderTextToCanvas()
    this.currentDataUrl = dataUrl
    this.props.onSignatureChange(dataUrl, this.selectedFont.family, this.text)
  }

  @action.bound
  private toggleStyleMenu() {
    this.isStyleMenuOpen = !this.isStyleMenuOpen
  }

  @action.bound
  private selectFont(font: SignatureFont) {
    this.selectedFont = font
    this.isStyleMenuOpen = false
    const dataUrl = this.renderTextToCanvas()
    this.currentDataUrl = dataUrl
    this.props.onSignatureChange(dataUrl, font.family, this.text)
  }

  private renderTextToCanvas(): string | null {
    const canvas = this.canvasRef.current
    if (!canvas || !this.text.trim()) return null

    const ctx = canvas.getContext('2d')
    if (!ctx) return null

    const { width = 400, height = 200 } = this.props

    ctx.clearRect(0, 0, width, height)

    ctx.fillStyle = '#000000'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'

    const fontSize = Math.min(48, width / (this.text.length * 0.5))
    ctx.font = `${fontSize}px ${this.selectedFont.family}`

    ctx.fillText(this.text, width / 2, height / 2)

    return canvas.toDataURL('image/png')
  }
}
